// ════════════════════════════════════════════════════════════════════════
//  Welche Lektion offen ist – und warum die anderen es nicht sind
//
//  Reine Rechnung aus Bundle, Fortschritt und der Regel, die die Klasse gewählt
//  hat. Kein Zustand, keine Abfrage, kein React: dieselbe Bauart wie `lock.ts`
//  eine Ebene höher, und aus demselben Grund prüfbar ohne Bildschirm.
//
//  Der Server sperrt nicht. Er nennt die Regel (`gate`, `gateStars`), weil nur
//  er weiß, was die Klasse eingestellt hat; gerechnet wird hier, weil nur hier
//  das Bundle liegt.
//
//  DIE ZWEITE REGEL IST DIE WICHTIGE
//
//  Eine begonnene Lektion bleibt offen. Ohne sie schlösse ein Fassungswechsel,
//  der eine Lektion in der Mitte einfügt, rückwirkend Lektionen, die das Kind
//  längst geübt hat – der Bildschirm eines Kindes darf nicht rückwärts gehen.
// ════════════════════════════════════════════════════════════════════════

import { lessonsInOrder, type Bundle, type Lesson } from "@otto/lehrgang";
import type { Progress } from "../../progress/lib/progress.js";
import { starsForLesson } from "./lessonStars.js";

/** Wie die Klasse ihre Lektionen freigibt. */
export type Gate =
  | { readonly kind: "frei" }
  /** `stars: null` heißt: jede Stufe einmal geübt – wie beim Lehrgangsabschluss. */
  | { readonly kind: "reihenfolge"; readonly stars: 1 | 2 | 3 | null };

/** Die Regel offen (`frei`), wenn nichts anderes feststeht. */
export const OPEN: Gate = { kind: "frei" };

/**
 * Was der Server geschickt hat, als Regel.
 *
 * Alles Unbekannte wird `frei`. Das gilt auch für `freigabe`: die Ausbaustufe
 * ist zurückgestellt, die Datenbank lässt den Wert aber schon zu. Eine App, die
 * darüber stolpert oder ihn als Sperre missversteht, wäre die schlechteste Art,
 * davon zu erfahren – ein Kind stünde vor einer Tür, die niemand öffnen kann.
 */
export function gateFrom(course: {
  readonly gate?: string | null;
  readonly gateStars?: number | null;
}): Gate {
  if (course.gate !== "reihenfolge") return OPEN;
  const stars = course.gateStars;
  return {
    kind: "reihenfolge",
    stars: stars === 1 || stars === 2 || stars === 3 ? stars : null,
  };
}

/** Hat das Kind hier schon etwas gemacht? */
function begonnen(lesson: Lesson, progress: Progress): boolean {
  return lesson.stages.some((stage) => progress[stage.key] !== undefined);
}

/** Jede Stufe mindestens einmal geübt. */
function durch(lesson: Lesson, progress: Progress): boolean {
  return lesson.stages.every((stage) => progress[stage.key] !== undefined);
}

/**
 * Reicht diese Lektion, um die nächste zu öffnen?
 *
 * Eine Lektion ohne Stufen reicht immer: sie ist nie zu schaffen und wäre sonst
 * eine Sperre, an der der ganze Lehrgang endet.
 */
function reichtAus(
  lesson: Lesson,
  progress: Progress,
  gate: Gate,
  bundle: Bundle,
): boolean {
  if (lesson.stages.length === 0) return true;
  if (gate.kind !== "reihenfolge") return true;
  if (gate.stars === null) return durch(lesson, progress);
  return (
    durch(lesson, progress) &&
    (starsForLesson(lesson, progress, bundle.starThresholds) ?? 0) >= gate.stars
  );
}

/**
 * Die Schlüssel der Lektionen, die das Kind jetzt öffnen darf.
 *
 * Vier Regeln, und jede hat einen Fall, an dem sie sich zeigt:
 *
 *   1. die ERSTE Lektion ist immer offen – sonst startet der Lehrgang gesperrt;
 *   2. eine begonnene Lektion bleibt offen – siehe oben;
 *   3. eine Lektion ohne Stufen sperrt nie;
 *   4. ohne Regel (`frei`) sind alle offen.
 */
export function openLessonKeys(
  bundle: Bundle,
  progress: Progress,
  gate: Gate,
): ReadonlySet<string> {
  const ordered = lessonsInOrder(bundle);
  if (gate.kind === "frei") return new Set(ordered.map((lesson) => lesson.key));

  const open = new Set<string>();
  let vorigeReicht = true;
  for (const lesson of ordered) {
    if (vorigeReicht || begonnen(lesson, progress)) open.add(lesson.key);
    vorigeReicht = open.has(lesson.key) && reichtAus(lesson, progress, gate, bundle);
  }
  return open;
}

/** Warum diese Lektion zu ist – oder null, wenn sie offen ist. */
export type LessonLock = {
  /** Die Lektion, die zuerst dran ist. */
  readonly after: Lesson;
  /** Wie viele Sterne sie braucht, oder null: dann reicht üben. */
  readonly stars: 1 | 2 | 3 | null;
};

export function lockOf(
  lesson: Lesson,
  bundle: Bundle,
  progress: Progress,
  gate: Gate,
): LessonLock | null {
  if (gate.kind !== "reihenfolge") return null;
  if (openLessonKeys(bundle, progress, gate).has(lesson.key)) return null;

  const ordered = lessonsInOrder(bundle);
  const position = ordered.findIndex((l) => l.key === lesson.key);
  const before = position > 0 ? ordered[position - 1] : null;
  if (!before) return null;

  // Fehlen nur noch Sterne, ist das der Grund; fehlen Stufen, ist es das Üben.
  const fehlenSterne = gate.stars !== null && durch(before, progress);
  return { after: before, stars: fehlenSterne ? gate.stars : null };
}
